/**
 * Overview Sidebar - Simplified navigation for Overview Dashboard
 * 
 * Shows only high-level options relevant to multi-retailer view.
 * Hides retailer-specific deep-dive links.
 */

import React from 'react';
import { useLocation } from 'wouter';
import { 
  LayoutDashboard,
  Building2, 
  TrendingUp,
  Sparkles,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  Zap
} from 'lucide-react';

interface OverviewSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function OverviewSidebar({ isOpen, onToggle }: OverviewSidebarProps) {
  const [location, setLocation] = useLocation();

  const navItems = [
    {
      section: 'Overview',
      items: [
        { 
          name: 'Brand Overview', 
          path: '/dashboard', 
          icon: LayoutDashboard,
          description: 'Multi-retailer performance'
        },
      ]
    },
    {
      section: 'Agents',
      items: [
        { 
          name: 'PIQ Command Center', 
          path: '/command-center', 
          icon: Zap,
          description: 'AI Intelligence Hub'
        },
      ]
    },
    {
      section: 'Analysis',
      items: [
        { 
          name: 'Cross-Retailer Insights', 
          path: '/cross-retailer-insights', 
          icon: TrendingUp,
          description: 'Compare retailers',
          disabled: true, // Future feature
        },
        { 
          name: 'Portfolio Health', 
          path: '/portfolio-health', 
          icon: Sparkles,
          description: 'Aggregate metrics',
          disabled: true, // Future feature
        },
      ]
    },
    {
      section: 'Admin',
      items: [
        { 
          name: 'User Management', 
          path: '/admin/users', 
          icon: Users,
          description: 'Manage access',
          disabled: true, // Future feature
        },
        { 
          name: 'Company Settings', 
          path: '/admin/company', 
          icon: Settings,
          description: 'Configure brand',
          disabled: true, // Future feature
        },
      ]
    }
  ];

  return (
    <div className={`
      fixed left-0 top-0 h-full bg-slate-900 text-white
      transition-all duration-300 z-30
      ${isOpen ? 'w-64' : 'w-16'}
    `}>
      {/* Header with Company Logo */}
      <div className="border-b border-slate-700">
        {isOpen && (
          <div className="px-4 pt-4 pb-3">
            {/* Company Logo Placeholder */}
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <img 
                  src="/assets/company-logos/buttah-skin.png"
                  alt="Buttah Skin"
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    // Fallback to text if logo not found
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.innerHTML = '<div class="text-indigo-600 font-bold text-xl">B</div>';
                  }}
                />
              </div>
              <div>
                <div className="font-bold text-white">Buttah Skin</div>
                <div className="text-xs text-slate-400">Brand Portfolio</div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between p-4 border-t border-slate-700">
          {isOpen && (
            <div className="flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-indigo-400" />
              <span className="font-semibold text-sm">Overview</span>
            </div>
          )}
          <button
            onClick={onToggle}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors ml-auto"
          >
            {isOpen ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-80px)]">
        {navItems.map((section) => (
          <div key={section.section}>
            {isOpen && (
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                {section.section}
              </h3>
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location === item.path;
                const isDisabled = item.disabled;

                return (
                  <button
                    key={item.path}
                    onClick={() => !isDisabled && setLocation(item.path)}
                    disabled={isDisabled}
                    className={`
                      w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg
                      transition-all duration-200 group
                      ${isActive 
                        ? 'bg-indigo-600 text-white shadow-lg' 
                        : isDisabled
                          ? 'text-slate-500 cursor-not-allowed'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }
                    `}
                    title={isOpen ? undefined : item.name}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${
                      isActive ? 'text-white' : 'text-slate-400'
                    }`} />
                    {isOpen && (
                      <div className="flex-1 text-left">
                        <div className="font-medium text-sm">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-slate-400 group-hover:text-slate-300">
                            {item.description}
                          </div>
                        )}
                      </div>
                    )}
                    {isDisabled && isOpen && (
                      <span className="text-xs bg-slate-700 px-2 py-0.5 rounded text-slate-400">
                        Soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Helper text */}
        {isOpen && (
          <div className="mt-8 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <p className="text-xs text-slate-400">
              💡 <span className="font-semibold text-slate-300">Tip:</span> Select a retailer from the dropdown above to access detailed analytics and deep-dive features.
            </p>
          </div>
        )}
      </nav>

      {/* Footer */}
      {isOpen && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700 bg-slate-900">
          <div className="text-xs text-slate-500">
            <div className="font-medium text-slate-400">Multi-Retailer Mode</div>
            <div>Aggregated view across all retailers</div>
          </div>
        </div>
      )}
    </div>
  );
}

